import { useEffect, useState } from 'react';

import { config } from '@/config';

/** How often an open tab asks whether a newer build has been deployed. */
export const NEW_BUILD_CHECK_MS = 5 * 60 * 1000;

/** The commit of the build the server is serving now, from build/version.json; null when unknown. */
export const fetchDeployedCommit = async (): Promise<string | null> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const body = await response.json();
    return typeof body?.commit === 'string' ? body.commit : null;
  } catch {
    return null;
  }
};

/**
 * The deployed commit once it differs from the one this tab runs, else null. Checks when the tab
 * comes back into view and every few minutes. Local builds have no deploy to compare with.
 */
export const useNewBuild = (current: string = config.COMMIT_SHA): string | null => {
  const [deployed, setDeployed] = useState<string | null>(null);

  useEffect(() => {
    if (current === 'local') {
      return;
    }
    let cancelled = false;
    let sequence = 0;
    const check = async () => {
      const request = ++sequence;
      const commit = await fetchDeployedCommit();
      if (!cancelled && request === sequence && commit && commit !== 'local') {
        setDeployed(commit === current ? null : commit);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    const interval = window.setInterval(check, NEW_BUILD_CHECK_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [current]);

  return deployed;
};

/**
 * Vite fires `vite:preloadError` when a lazily loaded chunk is missing, usually because this tab runs
 * an older build. Reload once per build to pick up the new one; if that still fails, let the error
 * reach the error boundary rather than reload in a loop.
 */
export const reloadOnceOnPreloadError =
  (
    storage?: Pick<Storage, 'getItem' | 'setItem'>,
    reload: () => void = () => window.location.reload(),
    commit: string = config.COMMIT_SHA,
  ) =>
  (event: Event) => {
    const key = `preload-error-reload:${commit}`;
    try {
      const session = storage ?? window.sessionStorage;
      if (session.getItem(key)) {
        return;
      }
      session.setItem(key, '1');
    } catch {
      // Without a persisted marker, let the error through to avoid a reload loop.
      return;
    }
    event.preventDefault();
    reload();
  };
